// FLAG:useDocuAi — which VAT facts block the tax category, and where they come from.
//
// The block itself is a server-side product decision: `vatJurisdiction` (built in
// api/services/_helpers/vatJurisdiction, wired into contractToAnalysis) determines at the receipt
// date whether bookingApi can represent a rate at all — the seller's country of residence
// explicitly does NOT block it. Nothing is re-derived here, or the analysis and UI could drift
// apart; this code only FINDS the finding.
//
// It must be found because the same receipt reaches the screen through two entry points and the
// facts live in different places along the way:
// • review component (/documents/review/:id) — build-review-data puts them on metaData.invoiceFacts;
// • expense/revenue form (/expenses/edit/:id) — the receipt itself carries no facts; they live on
// the attached file (metaData.ocr.invoiceFacts or documentInvoiceDetails).
// This was exactly why the block previously failed: it was tied to the review entry point rather
// than the receipt. The review component blocked the category, but the resulting expense in the
// „Offen" state offered it again — reported for an Austrian invoice with 20% VAT (Farben Morscher).
//
// `enabled` is the STRUCTURAL flag gate. Callers
// pass useDocuAi through; with flag-OFF it is false,
// the function returns null, and the block does not exist. This deliberately is not a data argument
// („no invoiceFacts exist with flag-OFF"): that is true today, but it is a claim about every writer
// of file metaData and therefore not something the flag guarantee should rely on.

export interface VatJurisdictionFact {
  bookable?: boolean
  reason?: string
  unbookableRates?: number[] | null
  allowedRates?: number[] | null
  isDomestic?: boolean
}

interface InvoiceFacts {
  vatJurisdiction?: VatJurisdictionFact | null
}

interface FactCarrier {
  invoiceFacts?: InvoiceFacts | null
}

interface RelatedFile {
  documentInvoiceDetails?: FactCarrier | null
  metaData?: (FactCarrier & { ocr?: FactCarrier | null }) | null
}

interface FactSources {
  /** metaData.invoiceFacts of the displayed receipt (populated by the review component). */
  invoiceFacts?: InvoiceFacts | null
  /** The reviewed record already loaded by the expense/revenue host. */
  reviewDetails?: FactCarrier | null
  /** The receipt's filesToAssign + receipts. */
  relatedFiles?: RelatedFile[] | null
}

/**
 * @returns The first finding that actually prevents booking — or null. The gate is always
 *   `bookable === false`, never `reason`: the reason only determines the notice wording.
 */
export const findUnbookableVatFact = (
  enabled: boolean,
  { invoiceFacts, reviewDetails, relatedFiles }: FactSources = {},
): VatJurisdictionFact | null => {
  if (!enabled) return null

  const candidates: (InvoiceFacts | null | undefined)[] = [
    invoiceFacts,
    reviewDetails?.invoiceFacts,
    ...(relatedFiles || []).flatMap(file => [
      file?.documentInvoiceDetails?.invoiceFacts,
      file?.metaData?.invoiceFacts,
      file?.metaData?.ocr?.invoiceFacts,
    ]),
  ]

  return candidates.find(facts => facts?.vatJurisdiction?.bookable === false)?.vatJurisdiction ?? null
}
